import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../utils/AuthContext";
import { useRouter } from "next/router";
import {
  Alert,
  BottomLink,
  JobDetails,
  Button,
  japanMoment,
  InfoPopup,
} from "@project/shared";
import { useMutation, useQuery } from "react-query";
import { FetchJobDetails } from "../../services/jobs";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Spin, Result } from "antd";
import { ScoutEntry } from "../../services/scout";
import { CreateEntry } from "@project/shared/src/service/entry";
import { ProfileMessage } from "@project/shared/src/components/molecules/MessageModal";
import Head from "next/head";
import PrivateRoute from "../../withPrivateRoute";

const StyledSpinner = styled.div`
  height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const StyledError = styled.div`
  height: 20vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledButton = styled(Button)`
  margin: auto;
`;

const JobInformation = () => {
  const { authenticated } = useContext(AuthContext);
  const router = useRouter();
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [isChat, setIsChat] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showButton, setShowButton] = useState(true);

  const handleClick = () => {
    if (!authenticated) {
      router.push("/login");
    }
  };
  const {
    data: jobInformation,
    refetch: refectchJobInformations,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery<any, any>(
    "fetch job information",
    () => FetchJobDetails(router.query?.id),
    {
      refetchOnWindowFocus: false,
      enabled: !!router?.query.id || !!authenticated,
    }
  );

  useEffect(() => {
    if (router?.query.id) {
      refectchJobInformations();
    }
  }, [router?.query.id]);

  const handleMessageClick = () => {
    setIsChat(true);
    if (
      japanMoment(jobInformation?.data?.member?.updated_at).diff(
        jobInformation?.data?.member?.created_at,
        "seconds"
      ) > 0 &&
      jobInformation?.data?.member?.name !== "" &&
      jobInformation?.data?.member?.name !== null
    ) {
      router.push(`/chat/${jobInformation?.data?.job?.company?.uuid}`);
    } else {
      setModalVisible(true);
    }
  };

  const { mutate: scoutMutate, isLoading: scoutLoading } = useMutation(
    ScoutEntry,
    {
      onSuccess: () => {
        Alert({
          type: "success",
          message: t("Success"),
        });

        if (jobInformation?.data?.member?.displayNext) {
          setVisible(true);
        } else {
          refectchJobInformations();
        }
      },
    }
  );
  const handleScoutEntry = () => {
    setIsChat(false);
    if (
      japanMoment(jobInformation?.data?.member?.updated_at).diff(
        jobInformation?.data?.member?.created_at,
        "seconds"
      ) > 0 &&
      jobInformation?.data?.member?.name !== "" &&
      jobInformation?.data?.member?.name !== null
    ) {
      scoutMutate({
        id: jobInformation?.data?.scout?.id,
        data: {
          isInterested: true,
        },
      });
    } else {
      setModalVisible(true);
    }
  };
  const handleRefer = () => {
    router.push(`/introduce/${jobInformation?.data?.job?.id}`);
  };
  const entryInformation = {
    jobId: jobInformation?.data?.job?.id,
    companyId: jobInformation?.data?.job?.companyID,
  };
  const { mutate, isLoading: entryLoading } = useMutation(CreateEntry, {
    onSuccess: () => {
      Alert({
        type: "success",
        message: t("Entered the job successfully."),
      });
      if (jobInformation?.data?.member?.displayNext) {
        setVisible(true);
      } else {
        refectchJobInformations();
      }
    },
    onError: (e: any) => {
      if (e?.data?.error?.message === "applications are currently closed.") {
        Alert({
          type: "error",
          message: t("Error occured"),
          description: t("Applications are currently closed."),
        });
      } else if (e?.error.message || e?.error.msg) {
        Alert({
          type: "error",
          message: t("Error occured"),
          description: t(e?.error.message),
        });
      } else {
        Alert({
          type: "error",
          message: t("Error occured"),
        });
      }
    },
  });
  const handleScoutCardAction = () => {
    if (jobInformation?.data?.member?.displayNext) {
      setVisible(true);
    } else {
      refectchJobInformations();
    }
  };
  const handleEntry = async () => {
    setIsChat(false);

    if (
      japanMoment(jobInformation?.data?.member?.updated_at).diff(
        jobInformation?.data?.member?.created_at,
        "seconds"
      ) > 0 &&
      jobInformation?.data?.member?.name !== "" &&
      jobInformation?.data?.member?.name !== null
    ) {
      mutate(entryInformation);
    } else {
      setModalVisible(true);
    }
  };

  const isDateExceed = (date) => {
    const todayDate = japanMoment().format();
    if (date) {
      return japanMoment(date).isBefore(todayDate);
    }
    return false;
  };

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="initial-scale=1.0, width=device-width"
          key="viewport"
        />
        <meta
          property="og:image"
          content={jobInformation?.data?.job?.details1ImageURL}
          key="ogimage"
        />
        <meta property="og:site_name" content={"Sidebizz"} key="ogsitename" />
        <meta
          property="og:title"
          content={jobInformation?.data?.job?.title}
          key="ogtitle"
        />
        <meta
          property="og:description"
          content={jobInformation?.data?.job?.aboutCompany}
          key="ogdesc"
        />
      </Head>
      {isLoading || isFetching ? (
        <StyledSpinner>
          <Spin />
        </StyledSpinner>
      ) : !isError ? (
        <JobDetails
          saveEntry={handleEntry}
          isApplicationBlocked={jobInformation?.data?.isBlocked}
          handleReffer={handleRefer}
          handleScoutCardAction={handleScoutCardAction}
          companyID={jobInformation?.data?.job?.companyID}
          isAdminOrCompany={false}
          message={jobInformation?.data?.scout?.message}
          scoutStatus={jobInformation?.data?.scout?.status}
          handleSaveScouts={handleScoutEntry}
          entryLoading={entryLoading}
          saveLoading={scoutLoading}
          isJobExceedPublishDate={
            isDateExceed(jobInformation?.data?.job?.publishEndAt) ||
            jobInformation?.data?.job?.publishStatus == "end"
          }
          isEntryDisabled={
            jobInformation?.data?.entry?.status === "" ? true : false
          }
          // the job carries the コネクト fields under their own keys
          connect={jobInformation?.data?.job}
          outsourcing={{
            area: jobInformation?.data?.job?.jobAreas,
            estimatedReward: jobInformation?.data?.job?.estimatedFee,
            intialCost: jobInformation?.data?.job?.initialFee,
            revenueSimulation: jobInformation?.data?.job?.feeSimulation,
            feeConditions: jobInformation?.data?.job?.feeConditions,
            workingHoursCondition:
              jobInformation?.data?.job?.workingHoursCondition,
          }}
          refferal={{
            operationDay: jobInformation?.data?.entry?.fee,
            rewardAmount: jobInformation?.data?.entry?.fee,
            rewardNotification: jobInformation?.data?.entry?.updated_at,
            acceptedAt: jobInformation?.data?.entry?.acceptedAt,
            status: jobInformation?.data?.entry?.status,
            id: jobInformation?.data?.job?.id,
            message: jobInformation?.data?.entry?.feeDetails,
            entryID: jobInformation?.data?.entry?.id,
            numberOfReferrals: jobInformation?.data?.entry?.numberOfReferrals,
            plan: jobInformation?.data?.job?.plan,
          }}
          overview={{
            title: jobInformation?.data?.job?.title,
            id: jobInformation?.data?.job?.id,
            planCategory: jobInformation?.data?.job?.planCategory,
            aboutCompany: jobInformation?.data?.job?.catchPhrase,
            companyName: jobInformation?.data?.job?.companyName,
            genra: jobInformation?.data?.job?.jobGenres,
            plan: jobInformation?.data?.job?.plan,
            applicationCategory: jobInformation?.data?.job?.applicationCategory,
          }}
          recruitment={{
            title1: jobInformation?.data?.job?.details1Title,
            description1: jobInformation?.data?.job?.details1Contents,
            image1: jobInformation?.data?.job?.details1ImageURL,
            title2: jobInformation?.data?.job?.details2Title,
            description2: jobInformation?.data?.job?.details2Contents,
            image2: jobInformation?.data?.job?.details2ImageURL,
            title3: jobInformation?.data?.job?.details3Title,
            description3: jobInformation?.data?.job?.details3Contents,
            image3: jobInformation?.data?.job?.details3ImageURL,
          }}
          companyProps={{
            aboutUs: jobInformation?.data?.job?.aboutCompany,
            companyName: jobInformation?.data?.job?.companyName,
            location: jobInformation?.data?.job?.address,
            established: jobInformation?.data?.job?.establishedAt,
            representative: jobInformation?.data?.job?.representativeName,
            personInCharge: jobInformation?.data?.job?.company?.supportPerson,
            phone: jobInformation?.data?.job?.phone,
            email: jobInformation?.data?.job?.email,
            siteURL: jobInformation?.data?.job?.websiteUrl,
            businessContent: jobInformation?.data?.job?.businessDescription,
          }}
          recruitmentConditions={{
            department: jobInformation?.data?.job?.jobDepartments,
            type: jobInformation?.data?.job?.jobIndustries,
            area: jobInformation?.data?.job?.jobAreas,
            estimatedReward: jobInformation?.data?.job?.estimatedFee,
            fixedReward: jobInformation?.data?.job?.fixedFee,
            acheivmentReward: jobInformation?.data?.job?.feeConditions,
            jobID: jobInformation?.data?.job?.id,
            companyID: jobInformation?.data?.job?.companyID,
            planCategory: jobInformation?.data?.job?.planCategory,
          }}
          documentURL={jobInformation?.data?.job?.documentURL}
          documentName={jobInformation?.data?.job?.documentName}
          documentUploadedAt={jobInformation?.data?.job?.documentUploadedAt}
          scoutID={jobInformation?.data?.scout?.id}
          handleAuth={handleClick}
          handleMessageClick={handleMessageClick}
          refetchInformation={refectchJobInformations}
          featuredJob={jobInformation?.data?.featuredJob}
          recommendedJobs={jobInformation?.data?.recommendedJobs}
          referralJobs={jobInformation?.data?.referralJobs}
          setShowButton={setShowButton}
        />
      ) : error?.status === 403 ? (
        <Result
          status="403"
          title="403"
          subTitle={t(error?.data?.error.message)}
          extra={
            <StyledButton onClick={() => router.push("/")}>
              {t("Back Home")}
            </StyledButton>
          }
        />
      ) : (
        <StyledError>
          <p>{t("An unexpected error occured")}</p>
        </StyledError>
      )}
      {showButton &&
        !isLoading &&
        !isFetching &&
        !isError &&
        jobInformation?.data?.job?.id && (
          <BottomLink
            entryLoading={entryLoading}
            isJobExceedPublishDate={isDateExceed(
              jobInformation?.data?.job?.publishEndAt
            )}
            saveEntry={handleEntry}
            handleRefer={handleRefer}
            savingScout={scoutLoading}
            handleScout={handleScoutEntry}
            scoutStatus={jobInformation?.data?.scout?.status}
            refetch={refectchJobInformations}
            jobID={jobInformation?.data?.job?.id}
            companyID={jobInformation?.data?.job?.companyID}
            isEntryDisabled={
              jobInformation?.data?.entry?.status === "confirming" ||
              jobInformation?.data?.entry?.status === "offered"
                ? true
                : false
            }
            isEntryNotDone={jobInformation?.data?.entry?.status === ""}
            referalStatus={jobInformation?.data?.entry?.status}
            planType={jobInformation?.data?.job?.plan}
            handleMessageClick={handleMessageClick}
            hideMessage={
              jobInformation?.data?.job?.applicationCategory === "connect"
            }
          />
        )}
      <InfoPopup
        onClose={() => {
          setVisible(false);
          refectchJobInformations();
        }}
        visible={visible}
      ></InfoPopup>
      <ProfileMessage
        onCancel={() => setModalVisible(false)}
        visible={modalVisible}
        isChat={isChat}
      />
    </>
  );
};

export default PrivateRoute(JobInformation);
